import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GridColDef } from "@mui/x-data-grid";
import { Box, FormControlLabel, Checkbox, FormHelperText, Tabs, Tab } from "@mui/material";
import Grid from '@mui/material/Grid';
import { GridCellParams } from "@mui/x-data-grid";
import SettingsIcon from "@mui/icons-material/Settings";
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BusinessIcon from '@mui/icons-material/Business';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import YardIcon from '@mui/icons-material/Yard';
import NfcIcon from '@mui/icons-material/Nfc';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Formik, Form } from "formik";
import * as Yup from "yup";
import GridTable from "../../components/GridTable";
import TextField from '../../components/TextField';
import Dropdown from '../../components/Dropdown';
import ActionMenu from '../../components/ActionMenu';
import Dialog from "../../components/Dialog";
import Loader from '../../components/Loader'
import Button from '../../components/Button'
import { successMessage, errorMessage } from '../../components/Toaster';
import { ApiResponse, CompanyData, CreateCompanyParams, UpdateCompanyParams, CompanyScanCount } from './interface'
import { getAllCompanies, updateCompany, deleteCompany, createCompany, getCompanyWiseScanCount } from "./api";
import { getErrorMessage } from "../../utils/error";

const downloadScanCountCsv = (data: CompanyScanCount[], y: number): void => {
    const headers = [
        "company_name",
        `operator_current_year_${y}`,
        `operator_last_year_${y - 1}`,
        `operator_before_${y - 1}`,
        "operator_total_scans",
        `all_current_year_${y}`,
        `all_last_year_${y - 1}`,
        `all_before_${y - 1}`,
        "all_total_scans",
    ];

    const esc = (v: string | number | null | undefined): string => {
        const s = String(v ?? "");
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const lines = (data || []).map((r: CompanyScanCount) =>
        [
            r.company_name,
            r.operator_current_year,
            r.operator_last_year,
            r.operator_before_last_year,
            r.operator_total_scans,
            r.all_current_year,
            r.all_last_year,
            r.all_before_last_year,
            r.all_total_scans,
        ].map(esc).join(",")
    );

    const blob = new Blob([[headers.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `Company_Wise_Scan_Count_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
};

const Company: React.FC = () => {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState<CompanyData[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [editCompany, setEditCompany] = useState<CompanyData | null>(null);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const enamTypeValues = ["DEFAULT_UPJAO", "DEFAULT_ENAM"]
    const [loading, setLoading] = useState({
        update: false,
        delete: false,
        create: false,
        fetch: false,
    });
    const [activeTab, setActiveTab] = useState<number>(0);
    const [scanCounts, setScanCounts] = useState<CompanyScanCount[]>([]);
    const [scanLoading, setScanLoading] = useState(false);
    const [scanLoaded, setScanLoaded] = useState(false);

    const year = new Date().getFullYear();
    const fmtCount = (v: number) => Number(v || 0).toLocaleString("en-IN");

    const validationSchema = Yup.object().shape({
        companyName: Yup.string().required('Company name is required'),
        displayName: Yup.string().required('Display name is required'),
        companyLogo: Yup.string()
            .url('Company logo must be a valid URL')
            .nullable(),
        responseType: Yup.string().required('Response type is required'),
    });

    // Columns for displaying companies
    const columns: GridColDef[] = [
        { field: "company_name", headerName: "Company Name", flex: 1.5 },
        { field: "user_count", headerName: "User Count", flex: 0.5 },
        { field: "response_type", headerName: "Flow Type", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <EditIcon color="primary" />,
                            tooltip: "Edit",
                            onClick: handleEdit,
                        },
                        {
                            icon: <DeleteIcon color="error" />,
                            tooltip: "Delete",
                            onClick: handleDelete,
                        },
                        {
                            icon: <AccountCircleIcon color="info" />,
                            tooltip: "Users",
                            onClick: handleViewUsers,
                        },
                        {
                            icon: <Inventory2Icon color="action" />,
                            tooltip: "Machines",
                            onClick: handleViewMachines,
                        },
                        {
                            icon: <YardIcon color="success" />,
                            tooltip: "Crops",
                            onClick: handleViewCrops,
                        },
                        {
                            icon: <LocationOnIcon color="secondary" />,
                            tooltip: "Location",
                            onClick: handleViewLocations,
                        },
                        {
                            icon: <SettingsIcon color="inherit" />,
                            tooltip: "Config",
                            onClick: handleViewConfig,
                        },
                        {
                            icon: <NfcIcon color="primary" />,
                            tooltip: "Metadata",
                            onClick: handleViewMetadata,
                        },
                    ]}
                />
            ),
            sortable: false,
            filterable: false,
            flex: 2,
        },
    ];

    const countCol = (field: keyof CompanyScanCount, headerName: string, bold = false): GridColDef => ({
        field,
        headerName,
        type: "number",
        flex: 0.7,
        headerAlign: "left",
        align: "left",
        renderCell: (params: GridCellParams) =>
            bold ? <b>{fmtCount(params.value as number)}</b> : fmtCount(params.value as number),
    });

    const scanColumns: GridColDef[] = [
        { field: "company_name", headerName: "Company Name", flex: 1.6 },
        countCol("operator_current_year", `Operator ${year}`),
        countCol("operator_last_year", `Operator ${year - 1}`),
        countCol("operator_before_last_year", `Operator < ${year - 1}`),
        countCol("operator_total_scans", "Operator Total", true),
        countCol("all_current_year", `All ${year}`),
        countCol("all_last_year", `All ${year - 1}`),
        countCol("all_before_last_year", `All < ${year - 1}`),
        countCol("all_total_scans", "All Total", true),
    ];

    useEffect(() => {
        const fetchCompanies = async () => {
            setLoading((prev) => ({ ...prev, fetch: true }));
            try {
                const companiesData: ApiResponse<CompanyData[]> = await getAllCompanies();
                if (companiesData.status && companiesData.data) {
                    setCompanies(companiesData.data);
                } else {
                    errorMessage(companiesData.message || "Failed to fetch companies.");
                }
            } catch (error) {
                console.error("Error fetching companies:", error);
                const message = getErrorMessage(error);
                errorMessage(message || 'An unexpected error occurred. Please try again.');
            } finally {
                setLoading((prev) => ({ ...prev, fetch: false }));
            }
        };

        fetchCompanies();
    }, []);

    const fetchScanCounts = async (): Promise<CompanyScanCount[] | null> => {
        setScanLoading(true);
        try {
            const res: ApiResponse<CompanyScanCount[]> = await getCompanyWiseScanCount();
            if (res?.status) {
                const data = res.data || [];
                setScanCounts(data);
                setScanLoaded(true);
                return data;
            }
            errorMessage(res?.message || "Failed to fetch scan counts.");
            return null;
        } catch (error) {
            console.error("Error fetching scan counts:", error);
            const message = getErrorMessage(error);
            errorMessage(message || 'Failed to fetch scan counts.');
            return null;
        } finally {
            setScanLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 1 && !scanLoaded && !scanLoading) {
            fetchScanCounts();
        }
    }, [activeTab]);

    const handleDownloadCsv = () => {
        if (scanLoading) return;
        if (!scanCounts.length) {
            errorMessage("No scan data to download.");
            return;
        }
        downloadScanCountCsv(scanCounts, year);
    };

    const handleViewMachines = (params: GridCellParams) => {
        const company_id = params.row.company_id;
        const company_name = params.row.company_name;
        navigate(`/company/${company_id}/machines`, {
            state: { company_name }
        });
    };

    const handleViewUsers = (params: GridCellParams) => {
        const company_id = params.row.company_id;
        const company_name = params.row.company_name;
        navigate(`/company/${company_id}/users`, {
            state: { company_name }
        });
    };

    const handleViewCrops = (params: GridCellParams) => {
        const company_id = params.row.company_id;
        const company_name = params.row.company_name;
        navigate(`/company/${company_id}/crops`, {
            state: { company_name }
        });
    };

    const handleViewLocations = (params: GridCellParams) => {
        const company_id = params.row.company_id;
        const company_name = params.row.company_name;
        navigate(`/company/${company_id}/locations`, {
            state: { company_name }
        });
    };

    const handleViewConfig = (params: GridCellParams) => {
        const company_id = params.row.company_id;
        const company_name = params.row.company_name;
        navigate(`/company/${company_id}/${company_name}/company_config`);
    };

    const handleViewMetadata = (params: GridCellParams) => {
        const company_id = params.row.company_id;
        const company_name = params.row.company_name;
        navigate(`/company/${company_id}/metadata`, {
            state: { company_name }
        });
    };

    // Open dialog to add or edit company
    const openDialog = () => {
        setDialogOpen(true);
    };

    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setEditCompany(null);
        setDialogOpen(false);
    };

    // Handle edit company
    const handleEdit = (params: GridCellParams) => {
        setEditCompany(params.row as CompanyData);
        openDialog();
    };

    // Handle delete company
    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteOpen(false);
        setItemToDelete(null);
    };

    // Confirm dialog action (create or update company)
    const confirmDialog = async (
        values: any,
        { resetForm }: { resetForm: () => void }
    ) => {
        let success = false;
        if (editCompany) {
            success = await handleUpdate(values);
        } else {
            success = await handleCreate(values);
        }
        if (success) {
            closeDialog(resetForm);
        }
    };

    // Create Company
    const handleCreate = async (values: any) => {
        setLoading((prev) => ({ ...prev, create: true }));
        try {
            const newCompany: CreateCompanyParams = {
                company_name: values.companyName.trim(),
                display_name: values.displayName.trim(),
                company_logo: values?.companyLogo ? values.companyLogo.trim() : null,
                gst_number: values?.gstNumber ? values.gstNumber.trim() : null,
                address: values?.address ? values.address.trim() : null,
                contact_no: values?.contactNo ? values.contactNo.trim() : null,
                response_type: values.responseType,
                is_pdf_protected: values.isPdfProtected
            };
            const response = await createCompany(newCompany);

            if (response?.status) {
                const newCompany = {
                    company_id: response?.data?.company_id,
                    company_name: values.companyName,
                    display_name: values.displayName,
                    company_logo: values.companyLogo,
                    gst_number: values.gstNumber,
                    address: values.address,
                    contact_no: values.contactNo,
                    response_type: values.responseType,
                    is_pdf_protected: values.isPdfProtected,
                };
                setCompanies((prevCompanies) => {
                    const currentCompanies = Array.isArray(prevCompanies) ? prevCompanies : [];
                    return [...currentCompanies, newCompany];
                });
                successMessage(response.message || "Company created successfully!");
                return true;
            } else {
                errorMessage(response?.message || "Failed to create company. Please try again.");
                return false;
            }
        } catch (error) {
            console.error("Error creating company:", error);
            const message = getErrorMessage(error);
            errorMessage(message || 'An unexpected error occurred. Please try again.');
            return false;
        } finally {
            setLoading((prev) => ({ ...prev, create: false }));
        }
    };

    // Update Company
    const handleUpdate = async (values: any) => {
        setLoading((prev) => ({ ...prev, update: true }));
        try {
            const updatedCompanyData: UpdateCompanyParams = {
                company_id: editCompany!.company_id,
                company_name: values.companyName.trim(),
                display_name: values.displayName.trim(),
                company_logo: values.companyLogo?.trim(),
                gst_number: values.gstNumber?.trim(),
                address: values.address?.trim(),
                contact_no: values.contactNo?.trim(),
                response_type: values.responseType,
                is_pdf_protected: values.isPdfProtected,
            };

            const response = await updateCompany(updatedCompanyData);

            if (response?.status) {
                setCompanies((prevCompanies) =>
                    prevCompanies.map((company) =>
                        company.company_id === updatedCompanyData.company_id
                            ? { ...company, ...updatedCompanyData }
                            : company
                    )
                );
                localStorage.setItem('company_name', updatedCompanyData.company_name);
                successMessage(response.message || "Company updated successfully!");
                return true;
            } else {
                errorMessage(response?.message || "Failed to update company. Please try again.");
                return false;
            }
        } catch (error) {
            console.error("Error updating company:", error);
            const message = getErrorMessage(error);
            errorMessage(message || 'An unexpected error occurred. Please try again.');
            return false;
        } finally {
            setLoading((prev) => ({ ...prev, update: false }));
        }
    };

    // Delete Company
    const handleDeleteConfirm = async () => {
        setLoading((prev) => ({ ...prev, delete: true }));
        if (itemToDelete) {
            try {
                const response = await deleteCompany(itemToDelete.row.company_id);

                if (response?.status) {
                    setCompanies((prevCompanies) =>
                        prevCompanies.filter((company) => company.company_id !== itemToDelete.row.company_id)
                    );
                    successMessage(response.message || "Company deleted successfully!");
                } else {
                    errorMessage(response?.message || "Failed to delete company. Please try again.");
                }
            } catch (error) {
                console.error("Error deleting company:", error);
                const message = getErrorMessage(error);
                errorMessage(message || 'An unexpected error occurred. Please try again.');
            } finally {
                setLoading((prev) => ({ ...prev, delete: false }));
                setDeleteOpen(false);
            }
        }
    };

    return (
        <>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box style={{ width: '100%', height: '50%' }}>
                    <main className="m-a-20">
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                flexWrap: "wrap",
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Tabs
                                    value={activeTab}
                                    onChange={(_, v: number) => {
                                        setActiveTab(v);
                                        setSearchQuery("");
                                    }}
                                    TabIndicatorProps={{ style: { display: "none" } }}
                                    sx={{
                                        minHeight: 40,
                                        p: "4px",
                                        borderRadius: "10px",
                                        bgcolor: "#dde5f0",
                                        "& .MuiTab-root": {
                                            minHeight: 32,
                                            px: 2.5,
                                            py: 0.5,
                                            borderRadius: "8px",
                                            textTransform: "none",
                                            fontWeight: 600,
                                            fontSize: 14,
                                            color: "#475569",
                                        },
                                        "& .Mui-selected": {
                                            bgcolor: "#fff",
                                            color: "#1d5fa8 !important",
                                            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.12)",
                                        },
                                    }}
                                >
                                    <Tab label="Company Details" disableRipple />
                                    <Tab label="Scan Count" disableRipple />
                                </Tabs>
                                <Box sx={{ width: 300 }}>
                                    <TextField
                                        className='w-100'
                                        label="Search"
                                        variant="outlined"
                                        value={searchQuery}
                                        size="small"
                                        startIcon={<SearchIcon />}
                                        onChange={(e) => setSearchQuery(e.target.value)} />
                                </Box>
                            </Box>
                            {activeTab === 0 ? (
                                <Button
                                    label="Add"
                                    variant="contained"
                                    color="primary"
                                    onClick={openDialog}
                                />
                            ) : (
                                <Button
                                    label="Download CSV"
                                    variant="contained"
                                    color="primary"
                                    onClick={handleDownloadCsv}
                                />
                            )}
                        </Box>
                        <div style={{ height: '100%', width: '100%' }} className="ag-theme-alpine m-t-20">
                            {activeTab === 0 ? (
                                <GridTable
                                    rows={companies}
                                    columns={columns}
                                    pageSize={10}
                                    searchQuery={searchQuery}
                                    setSearchQuery={setSearchQuery}
                                    getRowId={(row) => row.company_id}
                                    density="standard"
                                />
                            ) : scanLoading ? (
                                <Loader color="primary" size={40} />
                            ) : (
                                <GridTable
                                    rows={scanCounts}
                                    columns={scanColumns}
                                    pageSize={10}
                                    searchQuery={searchQuery}
                                    setSearchQuery={setSearchQuery}
                                    getRowId={(row) => row.company_id}
                                    density="standard"
                                />
                            )}
                        </div>
                        <Formik
                            initialValues={{
                                companyName: editCompany ? editCompany.company_name : "",
                                companyLogo: editCompany ? editCompany.company_logo : null,
                                displayName: editCompany ? editCompany.display_name : "",
                                gstNumber: editCompany ? editCompany.gst_number : null,
                                contactNo: editCompany ? editCompany.contact_no : null,
                                address: editCompany ? editCompany.address : null,
                                responseType: editCompany ? editCompany.response_type : null,
                                isPdfProtected: editCompany ? editCompany.is_pdf_protected : false,
                            }}
                            validationSchema={validationSchema}
                            onSubmit={confirmDialog}
                            enableReinitialize
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleChange,
                                handleSubmit,
                                resetForm,
                                setFieldValue,
                            }) => (
                                <>
                                    <Dialog
                                        open={isDialogOpen}
                                        onClose={() => closeDialog(resetForm)}
                                        onConfirm={handleSubmit}
                                        title={
                                            !loading.update && !loading.create
                                                ? editCompany
                                                    ? "Edit Company"
                                                    : "Add New Company"
                                                : ""
                                        }
                                        confirmButtonText={
                                            !loading.update && !loading.create
                                                ? editCompany
                                                    ? "Update"
                                                    : "Add"
                                                : ""
                                        }
                                        cancelButtonText={!loading.update && !loading.create ? "Cancel" : ""}
                                        icon={<BusinessIcon />}
                                        message={
                                            <Box padding={4}>
                                                {loading.update || loading.create ? (
                                                    <Loader color="primary" size={40} />
                                                ) : (
                                                    <Form>
                                                        <Grid container spacing={2}>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="companyName"
                                                                    name="companyName"
                                                                    label="Company Name"
                                                                    required
                                                                    value={values.companyName}
                                                                    onChange={handleChange}
                                                                    error={touched.companyName && Boolean(errors.companyName)}
                                                                    helperText={touched.companyName && errors.companyName}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="displayName"
                                                                    name="displayName"
                                                                    label="Display Name"
                                                                    required
                                                                    value={values.displayName}
                                                                    onChange={handleChange}
                                                                    error={touched.displayName && Boolean(errors.displayName)}
                                                                    helperText={touched.displayName && errors.displayName}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <Dropdown
                                                                    options={enamTypeValues}
                                                                    value={values.responseType ?? ""}
                                                                    onChange={(option) => setFieldValue("responseType", option)}
                                                                    label="Flow Type"
                                                                    required
                                                                    error={touched.responseType && Boolean(errors.responseType)}
                                                                    helperText={touched.responseType && errors.responseType ? String(errors.responseType) : ""}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="companyLogo"
                                                                    name="companyLogo"
                                                                    label="Company Logo"
                                                                    value={values.companyLogo ?? ""}
                                                                    onChange={handleChange}
                                                                    error={touched.companyLogo && Boolean(errors.companyLogo)}
                                                                    helperText={touched.companyLogo && errors.companyLogo}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="gstNumber"
                                                                    name="gstNumber"
                                                                    label="GST Number"
                                                                    value={values.gstNumber ?? ""}
                                                                    onChange={handleChange}
                                                                    error={touched.gstNumber && Boolean(errors.gstNumber)}
                                                                    helperText={touched.gstNumber && errors.gstNumber}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="contactNo"
                                                                    name="contactNo"
                                                                    label="Contact Number"
                                                                    value={values.contactNo ?? ""}
                                                                    onChange={handleChange}
                                                                    error={touched.contactNo && Boolean(errors.contactNo)}
                                                                    helperText={touched.contactNo && errors.contactNo}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <TextField
                                                                    id="address"
                                                                    name="address"
                                                                    label="Address"
                                                                    value={values.address ?? ""}
                                                                    onChange={handleChange}
                                                                    error={touched.address && Boolean(errors.address)}
                                                                    helperText={touched.address && errors.address}
                                                                />
                                                            </Grid>
                                                            <Grid size={4}>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            id="isPdfProtected"
                                                                            name="isPdfProtected"
                                                                            checked={values.isPdfProtected}
                                                                            onChange={handleChange}
                                                                            color="primary"
                                                                        />
                                                                    }
                                                                    label="Is PDF Protected?"
                                                                />
                                                                {touched.isPdfProtected && errors.isPdfProtected && (
                                                                    <FormHelperText error>{errors.isPdfProtected}</FormHelperText>
                                                                )}
                                                            </Grid>
                                                        </Grid>
                                                    </Form>
                                                )}
                                            </Box>
                                        }
                                    />
                                </>
                            )}
                        </Formik>
                        <Dialog
                            open={deleteOpen}
                            onClose={handleDeleteCancel}
                            onConfirm={handleDeleteConfirm}
                            title={!loading.delete ? "Delete Company" : ""}
                            confirmButtonText={!loading.delete ? "Confirm" : ""}
                            cancelButtonText={!loading.delete ? "Cancel" : ""}
                            icon={<DeleteIcon />}
                            message={
                                <Box padding={2}>
                                    {loading.delete ? (
                                        <Loader
                                            color="primary"
                                            size={40}
                                        />
                                    ) : (
                                        "Are you sure you want to delete this company?"
                                    )}
                                </Box>
                            }
                        />
                    </main>
                </Box>
            )}
        </>
    );

};

export default Company;